import { getLocalDateKeyFromMixedTimestamp } from "./dateUtils";

/* ────────────────────────────────────────────────────────────
   Shared work-location (WFH / WFO) helpers.

   WHY THIS EXISTS
   The exported sheet used its own copy of this logic and left WFH/WFO cells
   blank for rows that clearly showed hours on screen. The two copies differed
   in two ways:

   1. The export only emitted hours when the day entry carried a
      `locationTypes` array containing "WFH" / "WFO". The UI never looks at
      that field, so whenever the API returns hours without it (which is what
      happens for most users in the "All" listing) the sheet came out empty.
   2. The export rebuilt the date key from the month name + `detail.day`,
      while the UI resolves it from the row's own `detail.date` timestamp and
      only falls back to the day number.

   Both screens now go through these helpers, so the workbook always contains
   exactly what the UI displays. NOTE: display strings are produced here too,
   which keeps the on-screen table and the sheet in sync.
──────────────────────────────────────────────────────────── */

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type WorkLocationDay = Record<string, any>;
export type WorkLocationDayMap = Record<string, WorkLocationDay>;

interface WorkLocationOwner {
  email?: string;
  id?: number | string;
}

interface TimesheetLike extends WorkLocationOwner {
  year: number | string;
  month: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data?: any[];
}

const DATE_KEY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/** A map keyed by user (email / id) rather than by date. */
const isNestedByUser = (map: Record<string, unknown>) =>
  !Object.keys(map).some((key) => DATE_KEY_PATTERN.test(key));

/**
 * Picks one user's date-keyed map out of the shared state.
 * Falls back to treating the map as a single user's flat map (legacy callers).
 */
export const resolveUserWorkLocations = (
  workLocations: Record<string, unknown> | undefined | null,
  owner: WorkLocationOwner
): WorkLocationDayMap => {
  if (!workLocations) return {};
  if (!isNestedByUser(workLocations)) return workLocations as WorkLocationDayMap;

  const byEmail = owner?.email ? workLocations[owner.email] : undefined;
  const byId =
    owner?.id !== undefined && owner?.id !== null
      ? workLocations[String(owner.id)]
      : undefined;

  return (byEmail || byId || {}) as WorkLocationDayMap;
};

/**
 * Finds the work-location entry for a timesheet row: the row's own date first,
 * then any loaded date whose day-of-month matches.
 */
export const getWorkLocationDayEntry = (
  userWorkLocations: WorkLocationDayMap | undefined | null,
  day: number,
  detailDate?: unknown
): WorkLocationDay | undefined => {
  if (!userWorkLocations) return undefined;

  let matchedKey = detailDate ? getLocalDateKeyFromMixedTimestamp(detailDate) || "" : "";

  if (!matchedKey || !userWorkLocations[matchedKey]) {
    matchedKey =
      Object.keys(userWorkLocations).find(
        (key) => Number(key.split("-")[2]) === Number(day)
      ) || "";
  }

  return matchedKey ? userWorkLocations[matchedKey] : undefined;
};

const toHourDisplay = (value: unknown, placeholder: string) =>
  value === null || value === undefined || value === "" ? placeholder : `${value}h`;

/** WFH / WFO display strings for a timesheet row (same rules as the UI table). */
export const getWorkLocationHours = (
  userWorkLocations: WorkLocationDayMap | undefined | null,
  day: number,
  detailDate?: unknown,
  placeholder = "-"
) => {
  const entry = getWorkLocationDayEntry(userWorkLocations, day, detailDate);

  return {
    wfh: toHourDisplay(entry?.wfhHours, placeholder),
    wfo: toHourDisplay(entry?.wfoHours, placeholder),
  };
};

/** WFH / WFO display strings for a date that has no timesheet row at all. */
export const getWorkLocationHoursForDateKey = (
  userWorkLocations: WorkLocationDayMap | undefined | null,
  dateKey: string,
  placeholder = "-"
) => {
  const entry = userWorkLocations?.[dateKey];

  return {
    wfh: toHourDisplay(entry?.wfhHours, placeholder),
    wfo: toHourDisplay(entry?.wfoHours, placeholder),
  };
};

/** 1-based month number from a month name/abbreviation, or null when unparsable. */
export const getMonthNumber = (month: string, year: number | string): number | null => {
  const monthIndex = new Date(`${month} 1, ${year}`).getMonth();
  return Number.isNaN(monthIndex) ? null : monthIndex + 1;
};

/**
 * Dates that have work-location data for the user's month but no timesheet
 * row with hours — rendered as their own "no timesheet entry" section/sheet.
 */
export const getOrphanWorkLocationDates = (
  user: TimesheetLike,
  userWorkLocations: WorkLocationDayMap | undefined | null
): string[] => {
  if (!userWorkLocations) return [];

  const monthNumber = getMonthNumber(user.month, user.year);
  if (monthNumber === null) return [];

  const monthPrefix = `${user.year}-${String(monthNumber).padStart(2, "0")}-`;
  const wlKeys = Object.keys(userWorkLocations).filter((key) => key.startsWith(monthPrefix));
  if (!wlKeys.length) return [];

  const timesheetDayKeys = new Set<string>();
  (Array.isArray(user.data) ? user.data : []).forEach((project) => {
    (Array.isArray(project?.detail) ? project.detail : [])
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .filter((detail: any) => detail?.hour > 0)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .forEach((detail: any) => {
        // NOTE: day-number based key, kept exactly as the UI computed it so the
        // set of "no timesheet entry" dates does not change
        timesheetDayKeys.add(
          `${user.year}-${String(monthNumber).padStart(2, "0")}-${String(detail.day).padStart(2, "0")}`
        );
      });
  });

  return wlKeys.filter((key) => !timesheetDayKeys.has(key)).sort();
};
